import {
  EligibilityEvaluation,
  EligibilityResult,
  LocalizedReason,
} from '@plentifulapp/plentiful-wizard-engine';
import { debug } from '../../utils/logger';
import StateProgramsError from '../state-programs/errors/StateProgramsError';

/**
 * Response type for getAttestation call
 */
export interface AttestationResponse {
  visitId: string;
  programId: string;
  programVersion: string;
  eligibilityStatus: EligibilityResult;
  eligibilityReason?: LocalizedReason;
  answers: Record<string, any>;
}

/**
 * Facade for calling into the Attestation Service.
 */
export class AttestationFacade {
  private readonly baseUrl = 'proxy/attestationservice';

  private readonly submitEndpoint = '/submit-attestation';

  private readonly getEndpoint = '/get-attestation-data';

  constructor(
    private api: {
      post: any;
    }
  ) {
    // NOTE: the following comments do not apply while we use a proxy in our sails api to hit the attestation service
    // Initialize the facade with parameters specific to the Attestation Service stack being used (once it is hit directly)
    // (e.g., prod, staging, individual developer).
  }

  /**
   * Calls to get an attestation from the Attestation Service.
   * @param visitId Registered visit that the attestations is submitted to.
   * @param programId ID of the generalized state program that this is attesting to.
   * @param programVersion Version of the state program that these answers apply to.
   * @returns The attestation data if it exists, or null if no attestation exists (404).
   * @throws if there's an error other than a 404 (no attestation exists).
   */
  public async get(
    visitId: number,
    programId: string,
    programVersion: string
  ): Promise<AttestationResponse | null> {
    // only log answers to neighbor device on staging to prevent PII leakage
    debug(
      `Trying to get attestation request: ${JSON.stringify(
        {
          visitId,
          programId,
          programVersion,
        },
        undefined,
        2
      )}`
    );

    try {
      const response = await this.api.post(`${this.baseUrl}${this.getEndpoint}`, {
        visitId: String(visitId),
        programId,
        programVersion,
      });

      // Check response status or specific error codes
      if (response && response.status === 404) {
        return null;
      }
      if (response && response.status >= 400) {
        throw new Error(
          `Attestation service returned error status: ${response.status}, message: ${
            response.message || 'Unknown error'
          }`
        );
      }
      return response;
    } catch (error) {
      // Add context to the error before re-throwing
      const contextError = new StateProgramsError(
        'Failed to get attestation',
        error,
        programId,
        programVersion,
        visitId
      );

      // Log the error for debugging purposes
      debug(`Attestation get error: ${contextError.message}`);

      // Re-throw the error with context
      throw contextError;
    }
  }

  /**
   * Calls to submit an attestation to the Attestation Service.
   * @param neighborUserId User ID of the currently logged in neighbor.
   *        Used for which neighbor the attestation belongs to as well as the principal/actor that
   *        is submitting the attestation (given that these can't be different on the neighbor app).
   * @param visitId Registered visit that the attestations is submitted to.
   * @param providerId
   * @param programId ID of the generalized state program that this is attesting to.
   * @param programVersion Version of the state program that these answers apply to.
   * @param attestationAnswers Answers that the neighbor provided to the attestation questions.
   * @param eligibility
   * @throws iff the attestation was not saved to the service.
   */
  public async submit(
    neighborUserId: number,
    visitId: number,
    providerId: number,
    programId: string,
    programVersion: string,
    attestationAnswers: unknown,
    eligibility: EligibilityEvaluation
  ): Promise<void> {
    // only log answers to neighbor device on staging to prevent PII leakage
    debug(
      `Trying to submit attestation service request: ${JSON.stringify(
        {
          neighborUserId,
          visitId,
          providerId,
          programId,
          programVersion,
          attestationAnswers,
          eligibility,
        },
        undefined,
        2
      )}`
    );

    try {
      const response = await this.api.post(`${this.baseUrl}${this.submitEndpoint}`, {
        neighborId: String(neighborUserId),
        visitId: String(visitId),
        providerId: String(providerId),
        programId,
        programVersion,
        answers: attestationAnswers,
        eligibility: AttestationFacade.formatEligibility(eligibility),
      });

      // Check response status or specific error codes
      if (response && response.status >= 400) {
        throw new Error(
          `Attestation service returned error status: ${response.status}, message: ${
            response.message || 'Unknown error'
          }`
        );
      }
    } catch (error) {
      // Add context to the error before re-throwing
      const contextError = new StateProgramsError(
        'Failed to submit attestation',
        error,
        programId,
        programVersion,
        visitId,
        providerId
      );

      // Log the error for debugging purposes
      debug(`Attestation submission error: ${contextError.message}`);

      // Re-throw the error with context
      throw contextError;
    }
  }

  public static formatEligibility(eligibility: EligibilityEvaluation) {
    if (eligibility.result === 'eligible') {
      return { eligible: {} };
    }
    if (eligibility.result === 'ineligible') {
      return { ineligible: { reason: eligibility.ineligibleReasons || {} } };
    }
    if (eligibility.result === 'notApplicable') {
      return { notApplicable: {} };
    }
    return { unknown: {} };
  }
}
